'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { goldLeadsService, CreateGoldLeadData } from '@/services/goldLeadsService';
import { eventTrackingService } from '@/services/eventTrackingService';

interface CalculatorResult {
  weight: number;
  purity: number;
  estimatedValue: number;
  ourOffer: number;
}

interface FormData {
  weight: string;
  purity: string;
  fullName: string;
  address: string;
  postalCode: string;
  city: string;
  phone: string;
  email: string;
}

// Daily base prices (1g price) - Update these values daily - UNCHANGED
const BASE_PRICES: {[key: string]: number;} = {
  '24': 1366,
  '22': 1030,
  '20': 941,
  '18': 842,
  '14': 657,
  '8': 375
};

// 24k anchor points with exact target totals
const CURVE_24K = {
  grams: [1, 25, 30, 100, 200, 295, 300, 500],
  totals: [1366, 34150, 39900, 126150, 241150, 350400, 356150, 585650]
};

// 14k curve multipliers (derived from Gullbrev totals)
const CURVE_14K = {
  grams: [1, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100, 105],
  multiplier: [1.0, 2.692542, 4.520548, 6.689498, 8.692542, 10.972603, 13.270928, 15.955860, 18.157767, 20.354642, 22.554033, 26.068493, 28.389650, 30.710807, 33.031963, 35.353120, 42.724505, 50.607306, 53.560122, 68.245053, 71.821918, 75.398782]
};

// 18k curve multipliers (derived from Gullbrev totals)
const CURVE_18K = {
  grams: [1, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100, 105, 110, 115, 120, 125, 130, 135, 140, 145, 150, 155, 160, 165, 170, 175, 180, 185, 190, 195, 200],
  multiplier: [1.0, 2.695962, 4.527316, 6.686461, 8.694774, 10.976247, 13.261283, 15.940618, 18.137767, 20.334917, 22.532066, 26.140143, 28.467933, 30.795724, 33.123516, 35.451307, 42.659145, 50.582898, 53.532066, 68.318290, 71.900237, 75.482185, 79.064133, 82.646081, 86.228029, 89.809977, 93.391925, 96.973873, 100.555821, 104.137769, 126.287411, 130.491686, 134.695961, 138.900237, 143.104512, 147.308788, 151.513064, 155.717339, 159.921615, 164.125890, 200.0]
};

// Pricing lookup tables - First gram unchanged, all other grams increased with progressive multipliers
const PRICING_TABLE: {[karat: string]: {[gram: number]: number;};} = {
  '8': {
    1: 352, 2: 704, 3: 1056, 4: 1408, 5: 1760, 6: 2112, 7: 2464, 8: 2816, 9: 3168, 10: 3520,
    11: 3872, 12: 4224, 13: 4576, 14: 4928, 15: 5280, 16: 5632, 17: 5984, 18: 6336, 19: 6688, 20: 7040,
    21: 7392, 22: 7744, 23: 8096, 24: 8448, 25: 8800, 26: 9152, 27: 9504, 28: 9856, 29: 10208, 30: 10560,
    31: 10912, 32: 11264, 33: 11616, 34: 11968, 35: 12320, 36: 12672, 37: 13024, 38: 13376, 39: 13728, 40: 14080,
    41: 14432, 42: 14784, 43: 15136, 44: 15488, 45: 15840, 46: 16192, 47: 16544, 48: 16896, 49: 17248, 50: 17600,
    51: 17952, 52: 18304, 53: 18656, 54: 19008, 55: 19360, 56: 19712, 57: 20064, 58: 20416, 59: 20768, 60: 21120,
    61: 21472, 62: 21824, 63: 22176, 64: 22528, 65: 22880, 66: 23232, 67: 23584, 68: 23936, 69: 24288, 70: 24640,
    71: 24992, 72: 25344, 73: 25696, 74: 26048, 75: 26400, 76: 26752, 77: 27104, 78: 27456, 79: 27808, 80: 28160,
    81: 28512, 82: 28864, 83: 29216, 84: 29568, 85: 29920, 86: 30272, 87: 30624, 88: 30976, 89: 31328, 90: 31680,
    91: 32032, 92: 32384, 93: 32736, 94: 33088, 95: 33440, 96: 33792, 97: 34144, 98: 34496, 99: 34848, 100: 35200,
    101: 35552, 102: 35904, 103: 36256, 104: 36608, 105: 36960, 106: 37312, 107: 37664, 108: 38016, 109: 38368, 110: 38720,
    111: 39072, 112: 39424, 113: 39776, 114: 40128, 115: 40480, 116: 40832, 117: 41184, 118: 41536, 119: 41888, 120: 42240,
    121: 42592, 122: 42944, 123: 43296, 124: 43648, 125: 44000, 126: 44352, 127: 44704, 128: 45056, 129: 45408, 130: 45760,
    131: 46112, 132: 46464, 133: 46816, 134: 47168, 135: 47520, 136: 47872, 137: 48224, 138: 48576, 139: 48928, 140: 49280,
    141: 49632, 142: 49984, 143: 50336, 144: 50688, 145: 51040, 146: 51392, 147: 51744, 148: 52096, 149: 52448, 150: 52800,
    151: 53152, 152: 53504, 153: 53856, 154: 54208, 155: 54560, 156: 54912, 157: 55264, 158: 55616, 159: 55968, 160: 56320,
    161: 56672, 162: 57024, 163: 57376, 164: 57728, 165: 58080, 166: 58432, 167: 58784, 168: 59136, 169: 59488, 170: 59840,
    171: 60192, 172: 60544, 173: 60896, 174: 61248, 175: 61600, 176: 61952, 177: 62304, 178: 62656, 179: 63008, 180: 63360,
    181: 63712, 182: 64064, 183: 64416, 184: 64768, 185: 65120, 186: 65472, 187: 65824, 188: 66176, 189: 66528, 190: 66880,
    191: 67232, 192: 67584, 193: 67936, 194: 68288, 195: 68640, 196: 68992, 197: 69344, 198: 69696, 199: 70048, 200: 70400,
    201: 70752, 202: 71104, 203: 71456, 204: 71808, 205: 72160, 206: 72512, 207: 72864, 208: 73216, 209: 73568, 210: 73920,
    211: 74272, 212: 74624, 213: 74976, 214: 75328, 215: 75680, 216: 76032, 217: 76384, 218: 76736, 219: 77088, 220: 77440,
    221: 77792, 222: 78144, 223: 78496, 224: 78848, 225: 79200, 226: 79552, 227: 79904, 228: 80256, 229: 80608, 230: 80960,
    231: 81312, 232: 81664, 233: 82016, 234: 82368, 235: 82720, 236: 83072, 237: 83424, 238: 83776, 239: 84128, 240: 84480,
    241: 84832, 242: 85184, 243: 85536, 244: 85888, 245: 86240, 246: 86592, 247: 86944, 248: 87296, 249: 87648, 250: 88000,
    251: 88352, 252: 88704, 253: 89056, 254: 89408, 255: 89760, 256: 90112, 257: 90464, 258: 90816, 259: 91168, 260: 91520,
    261: 91872, 262: 92224, 263: 92576, 264: 92928, 265: 93280, 266: 93632, 267: 93984, 268: 94336, 269: 94688, 270: 95040,
    271: 95392, 272: 95744, 273: 96096, 274: 96448, 275: 96800, 276: 97152, 277: 97504, 278: 97856, 279: 98208, 280: 98560,
    281: 98912, 282: 99264, 283: 99616, 284: 99968, 285: 100320, 286: 100672, 287: 101024, 288: 101376, 289: 101728, 290: 102080,
    291: 102432, 292: 102784, 293: 103136, 294: 103488, 295: 103840, 296: 104192, 297: 104544, 298: 104896, 299: 105248, 300: 105600
  },
  14: {
    1: 651, 2: 1302, 3: 1953, 4: 2604, 5: 3255, 6: 3906, 7: 4557, 8: 5208, 9: 5859, 10: 6510,
    11: 7161, 12: 7812, 13: 8463, 14: 9114, 15: 9765, 16: 10416, 17: 11067, 18: 11718, 19: 12369, 20: 13020,
    21: 13671, 22: 14322, 23: 14973, 24: 15624, 25: 16275, 26: 16926, 27: 17577, 28: 18228, 29: 18879, 30: 19530,
    31: 20181, 32: 20832, 33: 21483, 34: 22134, 35: 22785, 36: 23436, 37: 24087, 38: 24738, 39: 25389, 40: 26040,
    41: 26691, 42: 27342, 43: 27993, 44: 28644, 45: 29295, 46: 29946, 47: 30597, 48: 31248, 49: 31899, 50: 32550,
    51: 33201, 52: 33852, 53: 34503, 54: 35154, 55: 35805, 56: 36456, 57: 37107, 58: 37758, 59: 38409, 60: 39060,
    61: 39711, 62: 40362, 63: 41013, 64: 41664, 65: 42315, 66: 42966, 67: 43617, 68: 44268, 69: 44919, 70: 45570,
    71: 46221, 72: 46872, 73: 47523, 74: 48174, 75: 48825, 76: 49476, 77: 50127, 78: 50778, 79: 51429, 80: 52080,
    81: 52731, 82: 53382, 83: 54033, 84: 54684, 85: 55335, 86: 55986, 87: 56637, 88: 57288, 89: 57939, 90: 58590,
    91: 59241, 92: 59892, 93: 60543, 94: 61194, 95: 61845, 96: 62496, 97: 63147, 98: 63798, 99: 64449, 100: 65100,
    101: 65751, 102: 66402, 103: 67053, 104: 67704, 105: 68355, 106: 69006, 107: 69657, 108: 70308, 109: 70959, 110: 71610,
    111: 72261, 112: 72912, 113: 73563, 114: 74214, 115: 74865, 116: 75516, 117: 76167, 118: 76818, 119: 77469, 120: 78120,
    121: 78771, 122: 79422, 123: 80073, 124: 80724, 125: 81375, 126: 82026, 127: 82677, 128: 83328, 129: 83979, 130: 84630,
    131: 85281, 132: 85932, 133: 86583, 134: 87234, 135: 87885, 136: 88536, 137: 89187, 138: 89838, 139: 90489, 140: 91140,
    141: 91791, 142: 92442, 143: 93093, 144: 93744, 145: 94395, 146: 95046, 147: 95697, 148: 96348, 149: 96999, 150: 97650,
    151: 98301, 152: 98952, 153: 99603, 154: 100254, 155: 100905, 156: 101556, 157: 102207, 158: 102858, 159: 103509, 160: 104160,
    161: 104811, 162: 105462, 163: 106113, 164: 106764, 165: 107415, 166: 108066, 167: 108717, 168: 109368, 169: 110019, 170: 110670,
    171: 111321, 172: 111972, 173: 112623, 174: 113274, 175: 113925, 176: 114576, 177: 115227, 178: 115878, 179: 116529, 180: 117180,
    181: 117831, 182: 118482, 183: 119133, 184: 119784, 185: 120435, 186: 121086, 187: 121737, 188: 122388, 189: 123039, 190: 123690,
    191: 124341, 192: 124992, 193: 125643, 194: 126294, 195: 126945, 196: 127596, 197: 128247, 198: 128898, 199: 129549, 200: 130200,
    201: 130851, 202: 131502, 203: 132153, 204: 132804, 205: 133455, 206: 134106, 207: 134757, 208: 135408, 209: 136059, 210: 136710,
    211: 137361, 212: 138012, 213: 138663, 214: 139314, 215: 139965, 216: 140616, 217: 141267, 218: 141918, 219: 142569, 220: 143220,
    221: 143871, 222: 144522, 223: 145173, 224: 145824, 225: 146475, 226: 147126, 227: 147777, 228: 148428, 229: 149079, 230: 149730,
    231: 150381, 232: 151032, 233: 151683, 234: 152334, 235: 152985, 236: 153636, 237: 154287, 238: 154938, 239: 155589, 240: 156240,
    241: 156891, 242: 157542, 243: 158193, 244: 158844, 245: 159495, 246: 160146, 247: 160797, 248: 161448, 249: 162099, 250: 162750,
    251: 163401, 252: 164052, 253: 164703, 254: 165354, 255: 166005, 256: 166656, 257: 167307, 258: 167958, 259: 168609, 260: 169260,
    261: 169911, 262: 170562, 263: 171213, 264: 171864, 265: 172515, 266: 173166, 267: 173817, 268: 174468, 269: 175119, 270: 175770,
    271: 176421, 272: 177072, 273: 177723, 274: 178374, 275: 179025, 276: 179676, 277: 180327, 278: 180978, 279: 181629, 280: 182280,
    281: 182931, 282: 183582, 283: 184233, 284: 184884, 285: 185535, 286: 186186, 287: 186837, 288: 187488, 289: 188139, 290: 188790,
    291: 189441, 292: 190092, 293: 190743, 294: 191394, 295: 192045, 296: 192696, 297: 193347, 298: 193998, 299: 194649, 300: 195300
  },
  18: {
    1: 820, 2: 1640, 3: 2460, 4: 3280, 5: 4100, 6: 4920, 7: 5740, 8: 6560, 9: 7380, 10: 8200,
    11: 9020, 12: 9840, 13: 10660, 14: 11480, 15: 12300, 16: 13120, 17: 13940, 18: 14760, 19: 15580, 20: 16400,
    21: 17220, 22: 18040, 23: 18860, 24: 19680, 25: 20500, 26: 21320, 27: 22140, 28: 22960, 29: 23780, 30: 24600,
    31: 25420, 32: 26240, 33: 27060, 34: 27880, 35: 28700, 36: 29520, 37: 30340, 38: 31160, 39: 31980, 40: 32800,
    41: 33620, 42: 34440, 43: 35260, 44: 36080, 45: 36900, 46: 37720, 47: 38540, 48: 39360, 49: 40180, 50: 41000,
    51: 41820, 52: 42640, 53: 43460, 54: 44280, 55: 45100, 56: 45920, 57: 46740, 58: 47560, 59: 48380, 60: 49200,
    61: 50020, 62: 50840, 63: 51660, 64: 52480, 65: 53300, 66: 54120, 67: 54940, 68: 55760, 69: 56580, 70: 57400,
    71: 58220, 72: 59040, 73: 59860, 74: 60680, 75: 61500, 76: 62320, 77: 63140, 78: 63960, 79: 64780, 80: 65600,
    81: 66420, 82: 67240, 83: 68060, 84: 68880, 85: 69700, 86: 70520, 87: 71340, 88: 72160, 89: 72980, 90: 73800,
    91: 74620, 92: 75440, 93: 76260, 94: 77080, 95: 77900, 96: 78720, 97: 79540, 98: 80360, 99: 81180, 100: 82000,
    101: 82820, 102: 83640, 103: 84460, 104: 85280, 105: 86100, 106: 86920, 107: 87740, 108: 88560, 109: 89380, 110: 90200,
    111: 91020, 112: 91840, 113: 92660, 114: 93480, 115: 94300, 116: 95120, 117: 95940, 118: 96760, 119: 97580, 120: 98400,
    121: 99220, 122: 100040, 123: 100860, 124: 101680, 125: 102500, 126: 103320, 127: 104140, 128: 104960, 129: 105780, 130: 106600,
    131: 107420, 132: 108240, 133: 109060, 134: 109880, 135: 110700, 136: 111520, 137: 112340, 138: 113160, 139: 113980, 140: 114800,
    141: 115620, 142: 116440, 143: 117260, 144: 118080, 145: 118900, 146: 119720, 147: 120540, 148: 121360, 149: 122180, 150: 123000,
    151: 123820, 152: 124640, 153: 125460, 154: 126280, 155: 127100, 156: 127920, 157: 128740, 158: 129560, 159: 130380, 160: 131200,
    161: 132020, 162: 132840, 163: 133660, 164: 134480, 165: 135300, 166: 136120, 167: 136940, 168: 137760, 169: 138580, 170: 139400,
    171: 140220, 172: 141040, 173: 141860, 174: 142680, 175: 143500, 176: 144320, 177: 145140, 178: 145960, 179: 146780, 180: 147600,
    181: 148420, 182: 149240, 183: 150060, 184: 150880, 185: 151700, 186: 152520, 187: 153340, 188: 154160, 189: 154980, 190: 155800,
    191: 156620, 192: 157440, 193: 158260, 194: 159080, 195: 159900, 196: 160720, 197: 161540, 198: 162360, 199: 163180, 200: 164000,
    201: 164820, 202: 165640, 203: 166460, 204: 167280, 205: 168100, 206: 168920, 207: 169740, 208: 170560, 209: 171380, 210: 172200,
    211: 173020, 212: 173840, 213: 174660, 214: 175480, 215: 176300, 216: 177120, 217: 177940, 218: 178760, 219: 179580, 220: 180400,
    221: 181220, 222: 182040, 223: 182860, 224: 183680, 225: 184500, 226: 185320, 227: 186140, 228: 186960, 229: 187780, 230: 188600,
    231: 189420, 232: 190240, 233: 191060, 234: 191880, 235: 192700, 236: 193520, 237: 194340, 238: 195160, 239: 195980, 240: 196800,
    241: 197620, 242: 198440, 243: 199260, 244: 200080, 245: 200900, 246: 201720, 247: 202540, 248: 203360, 249: 204180, 250: 205000,
    251: 205820, 252: 206640, 253: 207460, 254: 208280, 255: 209100, 256: 209920, 257: 210740, 258: 211560, 259: 212380, 260: 213200,
    261: 214020, 262: 214840, 263: 215660, 264: 216480, 265: 217300, 266: 218120, 267: 218940, 268: 219760, 269: 220580, 270: 221400,
    271: 222220, 272: 223040, 273: 223860, 274: 224680, 275: 225500, 276: 226320, 277: 227140, 278: 227960, 279: 228780, 280: 229600,
    281: 230420, 282: 231240, 283: 232060, 284: 232880, 285: 233700, 286: 234520, 287: 235340, 288: 236160, 289: 236980, 290: 237800,
    291: 238620, 292: 239440, 293: 240260, 294: 241080, 295: 241900, 296: 242720, 297: 243540, 298: 244360, 299: 245180, 300: 246000
  },
  '20': {
    1: 900, 2: 1800, 3: 2700, 4: 3600, 5: 4500, 6: 5400, 7: 6300, 8: 7200, 9: 8100, 10: 9000,
    11: 9900, 12: 10800, 13: 11700, 14: 12600, 15: 13500, 16: 14400, 17: 15300, 18: 16200, 19: 17100, 20: 18000,
    21: 18900, 22: 19800, 23: 20700, 24: 21600, 25: 22500, 26: 23400, 27: 24300, 28: 25200, 29: 26100, 30: 27000,
    31: 27900, 32: 28800, 33: 29700, 34: 30600, 35: 31500, 36: 32400, 37: 33300, 38: 34200, 39: 35100, 40: 36000,
    41: 36900, 42: 37800, 43: 38700, 44: 39600, 45: 40500, 46: 41400, 47: 42300, 48: 43200, 49: 44100, 50: 45000,
    51: 45900, 52: 46800, 53: 47700, 54: 48600, 55: 49500, 56: 50400, 57: 51300, 58: 52200, 59: 53100, 60: 54000,
    61: 54900, 62: 55800, 63: 56700, 64: 57600, 65: 58500, 66: 59400, 67: 60300, 68: 61200, 69: 62100, 70: 63000,
    71: 63900, 72: 64800, 73: 65700, 74: 66600, 75: 67500, 76: 68400, 77: 69300, 78: 70200, 79: 71100, 80: 72000,
    81: 72900, 82: 73800, 83: 74700, 84: 75600, 85: 76500, 86: 77400, 87: 78300, 88: 79200, 89: 80100, 90: 81000,
    91: 81900, 92: 82800, 93: 83700, 94: 84600, 95: 85500, 96: 86400, 97: 87300, 98: 88200, 99: 89100, 100: 90000,
    101: 90900, 102: 91800, 103: 92700, 104: 93600, 105: 94500, 106: 95400, 107: 96300, 108: 97200, 109: 98100, 110: 99000,
    111: 99900, 112: 100800, 113: 101700, 114: 102600, 115: 103500, 116: 104400, 117: 105300, 118: 106200, 119: 107100, 120: 108000,
    121: 108900, 122: 109800, 123: 110700, 124: 111600, 125: 112500, 126: 113400, 127: 114300, 128: 115200, 129: 116100, 130: 117000,
    131: 117900, 132: 118800, 133: 119700, 134: 120600, 135: 121500, 136: 122400, 137: 123300, 138: 124200, 139: 125100, 140: 126000,
    141: 126900, 142: 127800, 143: 128700, 144: 129600, 145: 130500, 146: 131400, 147: 132300, 148: 133200, 149: 134100, 150: 135000,
    151: 135900, 152: 136800, 153: 137700, 154: 138600, 155: 139500, 156: 140400, 157: 141300, 158: 142200, 159: 143100, 160: 144000,
    161: 144900, 162: 145800, 163: 146700, 164: 147600, 165: 148500, 166: 149400, 167: 150300, 168: 151200, 169: 152100, 170: 153000,
    171: 153900, 172: 154800, 173: 155700, 174: 156600, 175: 157500, 176: 158400, 177: 159300, 178: 160200, 179: 161100, 180: 162000,
    181: 162900, 182: 163800, 183: 164700, 184: 165600, 185: 166500, 186: 167400, 187: 168300, 188: 169200, 189: 170100, 190: 171000,
    191: 171900, 192: 172800, 193: 173700, 194: 174600, 195: 175500, 196: 176400, 197: 177300, 198: 178200, 199: 179100, 200: 180000,
    201: 180900, 202: 181800, 203: 182700, 204: 183600, 205: 184500, 206: 185400, 207: 186300, 208: 187200, 209: 188100, 210: 189000,
    211: 189900, 212: 190800, 213: 191700, 214: 192600, 215: 193500, 216: 194400, 217: 195300, 218: 196200, 219: 197100, 220: 198000,
    221: 198900, 222: 199800, 223: 200700, 224: 201600, 225: 202500, 226: 203400, 227: 204300, 228: 205200, 229: 206100, 230: 207000,
    231: 207900, 232: 208800, 233: 209700, 234: 210600, 235: 211500, 236: 212400, 237: 213300, 238: 214200, 239: 215100, 240: 216000,
    241: 216900, 242: 217800, 243: 218700, 244: 219600, 245: 220500, 246: 221400, 247: 222300, 248: 223200, 249: 224100, 250: 225000,
    251: 225900, 252: 226800, 253: 227700, 254: 228600, 255: 229500, 256: 230400, 257: 231300, 258: 232200, 259: 233100, 260: 234000,
    261: 234900, 262: 235800, 263: 236700, 264: 237600, 265: 238500, 266: 239400, 267: 240300, 268: 241200, 269: 242100, 270: 243000,
    271: 243900, 272: 244800, 273: 245700, 274: 246600, 275: 247500, 276: 248400, 277: 249300, 278: 250200, 279: 251100, 280: 252000,
    281: 252900, 282: 253800, 283: 254700, 284: 255600, 285: 256500, 286: 257400, 287: 258300, 288: 259200, 289: 260100, 290: 261000,
    291: 261900, 292: 262800, 293: 263700, 294: 264600, 295: 265500, 296: 266400, 297: 267300, 298: 268200, 299: 269100, 300: 270000
  },
  '22': {
    1: 999, 2: 1998, 3: 2997, 4: 3996, 5: 4995, 6: 5994, 7: 6993, 8: 7992, 9: 8991, 10: 9990,
    11: 10989, 12: 11988, 13: 12987, 14: 13986, 15: 14985, 16: 15984, 17: 16983, 18: 17982, 19: 18981, 20: 19980,
    21: 20979, 22: 21978, 23: 22977, 24: 23976, 25: 24975, 26: 25974, 27: 26973, 28: 27972, 29: 28971, 30: 29970,
    31: 30969, 32: 31968, 33: 32967, 34: 33966, 35: 34965, 36: 35964, 37: 36963, 38: 37962, 39: 38961, 40: 39960,
    41: 40959, 42: 41958, 43: 42957, 44: 43956, 45: 44955, 46: 45954, 47: 46953, 48: 47952, 49: 48951, 50: 49950,
    51: 50949, 52: 51948, 53: 52947, 54: 53946, 55: 54945, 56: 55944, 57: 56943, 58: 57942, 59: 58941, 60: 59940,
    61: 60939, 62: 61938, 63: 62937, 64: 63936, 65: 64935, 66: 65934, 67: 66933, 68: 67932, 69: 68931, 70: 69930,
    71: 70929, 72: 71928, 73: 72927, 74: 73926, 75: 74925, 76: 75924, 77: 76923, 78: 77922, 79: 78921, 80: 79920,
    81: 80919, 82: 81918, 83: 82917, 84: 83916, 85: 84915, 86: 85914, 87: 86913, 88: 87912, 89: 88911, 90: 89910,
    91: 90909, 92: 91908, 93: 92907, 94: 93906, 95: 94905, 96: 95904, 97: 96903, 98: 97902, 99: 98901, 100: 99900,
    101: 100899, 102: 101898, 103: 102897, 104: 103896, 105: 104895, 106: 105894, 107: 106893, 108: 107892, 109: 108891, 110: 109890,
    111: 110889, 112: 111888, 113: 112887, 114: 113886, 115: 114885, 116: 115884, 117: 116883, 118: 117882, 119: 118881, 120: 119880,
    121: 120879, 122: 121878, 123: 122877, 124: 123876, 125: 124875, 126: 125874, 127: 126873, 128: 127872, 129: 128871, 130: 129870,
    131: 130869, 132: 131868, 133: 132867, 134: 133866, 135: 134865, 136: 135864, 137: 136863, 138: 137862, 139: 138861, 140: 139860,
    141: 140859, 142: 141858, 143: 142857, 144: 143856, 145: 144855, 146: 145854, 147: 146853, 148: 147852, 149: 148851, 150: 149850,
    151: 150849, 152: 151848, 153: 152847, 154: 153846, 155: 154845, 156: 155844, 157: 156843, 158: 157842, 159: 158841, 160: 159840,
    161: 160839, 162: 161838, 163: 162837, 164: 163836, 165: 164835, 166: 165834, 167: 166833, 168: 167832, 169: 168831, 170: 169830,
    171: 170829, 172: 171828, 173: 172827, 174: 173826, 175: 174825, 176: 175824, 177: 176823, 178: 177822, 179: 178821, 180: 179820,
    181: 180819, 182: 181818, 183: 182817, 184: 183816, 185: 184815, 186: 185814, 187: 186813, 188: 187812, 189: 188811, 190: 189810,
    191: 190809, 192: 191808, 193: 192807, 194: 193806, 195: 194805, 196: 195804, 197: 196803, 198: 197802, 199: 198801, 200: 199800,
    201: 200799, 202: 201798, 203: 202797, 204: 203796, 205: 204795, 206: 205794, 207: 206793, 208: 207792, 209: 208791, 210: 209790,
    211: 210789, 212: 211788, 213: 212787, 214: 213786, 215: 214785, 216: 215784, 217: 216783, 218: 217782, 219: 218781, 220: 219780,
    221: 220779, 222: 221778, 223: 222777, 224: 223776, 225: 224775, 226: 225774, 227: 226773, 228: 227772, 229: 228771, 230: 229770,
    231: 230769, 232: 231768, 233: 232767, 234: 233766, 235: 234765, 236: 235764, 237: 236763, 238: 237762, 239: 238761, 240: 239760,
    241: 240759, 242: 241758, 243: 242757, 244: 243756, 245: 244755, 246: 245754, 247: 246753, 248: 247752, 249: 248751, 250: 249750,
    251: 250749, 252: 251748, 253: 252747, 254: 253746, 255: 254745, 256: 255744, 257: 256743, 258: 257742, 259: 258741, 260: 259740,
    261: 260739, 262: 261738, 263: 262737, 264: 263736, 265: 264735, 266: 265734, 267: 266733, 268: 267732, 269: 268731, 270: 269730,
    271: 270729, 272: 271728, 273: 272727, 274: 273726, 275: 274725, 276: 275724, 277: 276723, 278: 277722, 279: 278721, 280: 279720,
    281: 280719, 282: 281718, 283: 282717, 284: 283716, 285: 284715, 286: 285714, 287: 286713, 288: 287712, 289: 288711, 290: 289710,
    291: 290709, 292: 291708, 293: 292707, 294: 293706, 295: 294705, 296: 295704, 297: 296703, 298: 297702, 299: 298701, 300: 299700
  },
  '24': {
    1: 1380, 2: 2760, 3: 4140, 4: 5520, 5: 6900, 6: 8280, 7: 9660, 8: 11040, 9: 12420, 10: 13800,
    11: 15180, 12: 16560, 13: 17940, 14: 19320, 15: 20700, 16: 22080, 17: 23460, 18: 24840, 19: 26220, 20: 27600,
    21: 28980, 22: 30360, 23: 31740, 24: 33120, 25: 34500, 26: 35880, 27: 37260, 28: 38640, 29: 40020, 30: 41400,
    31: 42780, 32: 44160, 33: 45540, 34: 46920, 35: 48300, 36: 49680, 37: 51060, 38: 52440, 39: 53820, 40: 55200,
    41: 56580, 42: 57960, 43: 59340, 44: 60720, 45: 62100, 46: 63480, 47: 64860, 48: 66240, 49: 67620, 50: 69000,
    51: 70380, 52: 71760, 53: 73140, 54: 74520, 55: 75900, 56: 77280, 57: 78660, 58: 80040, 59: 81420, 60: 82800,
    61: 84180, 62: 85560, 63: 86940, 64: 88320, 65: 89700, 66: 91080, 67: 92460, 68: 93840, 69: 95220, 70: 96600,
    71: 97980, 72: 99360, 73: 100740, 74: 102120, 75: 103500, 76: 104880, 77: 106260, 78: 107640, 79: 109020, 80: 110400,
    81: 111780, 82: 113160, 83: 114540, 84: 115920, 85: 117300, 86: 118680, 87: 120060, 88: 121440, 89: 122820, 90: 124200,
    91: 125580, 92: 126960, 93: 128340, 94: 129720, 95: 131100, 96: 132480, 97: 133860, 98: 135240, 99: 136620, 100: 138000,
    101: 139380, 102: 140760, 103: 142140, 104: 143520, 105: 144900, 106: 146280, 107: 147660, 108: 149040, 109: 150420, 110: 151800,
    111: 153180, 112: 154560, 113: 155940, 114: 157320, 115: 158700, 116: 160080, 117: 161460, 118: 162840, 119: 164220, 120: 165600,
    121: 166980, 122: 168360, 123: 169740, 124: 171120, 125: 172500, 126: 173880, 127: 175260, 128: 176640, 129: 178020, 130: 179400,
    131: 180780, 132: 182160, 133: 183540, 134: 184920, 135: 186300, 136: 187680, 137: 189060, 138: 190440, 139: 191820, 140: 193200,
    141: 194580, 142: 195960, 143: 197340, 144: 198720, 145: 200100, 146: 201480, 147: 202860, 148: 204240, 149: 205620, 150: 207000,
    151: 208380, 152: 209760, 153: 211140, 154: 212520, 155: 213900, 156: 215280, 157: 216660, 158: 218040, 159: 219420, 160: 220800,
    161: 222180, 162: 223560, 163: 224940, 164: 226320, 165: 227700, 166: 229080, 167: 230460, 168: 231840, 169: 233220, 170: 234600,
    171: 235980, 172: 237360, 173: 238740, 174: 240120, 175: 241500, 176: 242880, 177: 244260, 178: 245640, 179: 247020, 180: 248400,
    181: 249780, 182: 251160, 183: 252540, 184: 253920, 185: 255300, 186: 256680, 187: 258060, 188: 259440, 189: 260820, 190: 262200,
    191: 263580, 192: 264960, 193: 266340, 194: 267720, 195: 269100, 196: 270480, 197: 271860, 198: 273240, 199: 274620, 200: 276000,
    201: 277380, 202: 278760, 203: 280140, 204: 281520, 205: 282900, 206: 284280, 207: 285660, 208: 287040, 209: 288420, 210: 289800,
    211: 291180, 212: 292560, 213: 293940, 214: 295320, 215: 296700, 216: 298080, 217: 299460, 218: 300840, 219: 302220, 220: 303600,
    221: 304980, 222: 306360, 223: 307740, 224: 309120, 225: 310500, 226: 311880, 227: 313260, 228: 314640, 229: 316020, 230: 317400,
    231: 318780, 232: 320160, 233: 321540, 234: 322920, 235: 324300, 236: 325680, 237: 327060, 238: 328440, 239: 329820, 240: 331200,
    241: 332580, 242: 333960, 243: 335340, 244: 336720, 245: 338100, 246: 339480, 247: 340860, 248: 342240, 249: 343620, 250: 345000,
    251: 346380, 252: 347760, 253: 349140, 254: 350520, 255: 351900, 256: 353280, 257: 354660, 258: 356040, 259: 357420, 260: 358800,
    261: 360180, 262: 361560, 263: 362940, 264: 364320, 265: 365700, 266: 367080, 267: 368460, 268: 369840, 269: 371220, 270: 372600,
    271: 373980, 272: 375360, 273: 376740, 274: 378120, 275: 379500, 276: 380880, 277: 382260, 278: 383640, 279: 385020, 280: 386400,
    281: 387780, 282: 389160, 283: 390540, 284: 391920, 285: 393300, 286: 394680, 287: 396060, 288: 397440, 289: 398820, 290: 400200,
    291: 401580, 292: 402960, 293: 404340, 294: 405720, 295: 407100, 296: 408480, 297: 409860, 298: 411240, 299: 412620, 300: 414000
  }

};

const GoldPriceCalculator = () => {
  const [formData, setFormData] = useState<FormData>({
    weight: '1',
    purity: '24',
    fullName: '',
    address: '',
    postalCode: '',
    city: '',
    phone: '',
    email: ''
  });
  const [calculatorResult, setCalculatorResult] = useState<CalculatorResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [errors, setErrors] = useState<{[key: string]: string;}>({});
  const [submitError, setSubmitError] = useState<string>('');

  // Track if user has started typing (to avoid duplicate events)
  const hasStartedTyping = useRef(false);

  // Linear interpolation helper
  const linearInterpolate = (x: number, x1: number, x2: number, y1: number, y2: number): number => {
    return y1 + (x - x1) * (y2 - y1) / (x2 - x1);
  };

  // Get price from lookup table with linear interpolation
  const getPriceFromTable = (grams: number, karat: string): number => {
    const table = PRICING_TABLE[karat];

    // If exact match exists
    if (table[grams] !== undefined) {
      return table[grams];
    }

    // Find surrounding data points for interpolation
    const availableGrams = Object.keys(table).map(Number).sort((a, b) => a - b);

    let lowerGram = availableGrams[0];
    let upperGram = availableGrams[availableGrams.length - 1];

    for (let i = 0; i < availableGrams.length - 1; i++) {
      if (grams >= availableGrams[i] && grams <= availableGrams[i + 1]) {
        lowerGram = availableGrams[i];
        upperGram = availableGrams[i + 1];
        break;
      }
    }

    // Linear interpolation between the two surrounding points
    return linearInterpolate(
      grams,
      lowerGram,
      upperGram,
      table[lowerGram],
      table[upperGram]
    );
  };

  // Calculate total payout using new pricing table
  const calculateTotalPayout = (grams: number, karat: string): number => {
    // Cap at 300g
    const effectiveGrams = Math.min(grams, 300);
    return getPriceFromTable(effectiveGrams, karat);
  };

  const purityOptions = [
  { value: '8', label: '8k' },
  { value: '14', label: '14k' },
  { value: '18', label: '18k' },
  { value: '20', label: '20k' },
  { value: '22', label: '22k' },
  { value: '24', label: '24k' }];


  // Automatic calculation when weight or purity changes
  useEffect(() => {
    const weight = parseFloat(formData.weight);

    if (weight > 0) {
      const totalPayout = calculateTotalPayout(weight, formData.purity);

      setCalculatorResult({
        weight,
        purity: parseFloat(formData.purity),
        estimatedValue: totalPayout,
        ourOffer: totalPayout
      });
    } else {
      setCalculatorResult(null);
    }
  }, [formData.weight, formData.purity]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }

    // Track first typing event in lead form (invisible tracking)
    if (!hasStartedTyping.current &&
    ['fullName', 'address', 'postalCode', 'city', 'phone', 'email'].includes(name) &&
    value.trim().length > 0) {
      hasStartedTyping.current = true;
      eventTrackingService.trackLeadFormTypingStart(name);
    }
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, weight: value }));
  };

  const handlePurityClick = (value: string) => {
    setFormData((prev) => ({ ...prev, purity: value }));
  };

  const validateContactForm = (): boolean => {
    const newErrors: {[key: string]: string;} = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'For- og etternavn er påkrevd';
    } else {
      // Check if full name has at least 2 words (first and last name)
      const nameParts = formData.fullName.trim().split(/\s+/);
      if (nameParts.length < 2) {
        newErrors.fullName = 'Vennligst oppgi både fornavn og etternavn';
      }
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Adresse er påkrevd';
    }

    if (!formData.postalCode.trim()) {
      newErrors.postalCode = 'Postnummer er påkrevd';
    } else if (!/^[0-9]{4}$/.test(formData.postalCode)) {
      newErrors.postalCode = 'Ugyldig postnummer (4 siffer)';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'Sted er påkrevd';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer er påkrevd';
    } else if (!/^[0-9]{8}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Ugyldig telefonnummer (8 siffer)';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-post er påkrevd';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ugyldig e-postadresse';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('=== FORM SUBMISSION STARTED ===');
    console.log('Form data:', formData);
    console.log('Calculator result:', calculatorResult);

    // Clear any previous errors first
    setSubmitError('');

    if (!validateContactForm()) {
      console.log('Validation failed with errors:', errors);
      setSubmitError('Vennligst fyll ut alle påkrevde felt korrekt');
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.getElementById(firstErrorField);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      return;
    }

    if (!calculatorResult || calculatorResult.weight <= 0) {
      setSubmitError('Vennligst velg vekten på gullet ditt (bruk sliderer)');
      console.log('Invalid calculator result:', calculatorResult);
      // Scroll to calculator
      document.getElementById('weight')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    console.log('Starting submission process...');

    try {
      // Split full name into first and last name
      const nameParts = formData.fullName.trim().split(/\s+/);
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ');

      // Prepare lead data
      const leadData: CreateGoldLeadData = {
        firstName: firstName,
        lastName: lastName,
        address: formData.address,
        postalCode: formData.postalCode,
        city: formData.city,
        phone: formData.phone,
        email: formData.email,
        weight: calculatorResult.weight,
        karat: calculatorResult.purity,
        estimatedValue: calculatorResult.ourOffer
      };

      console.log('Prepared lead data:', leadData);

      // Store in database
      console.log('Attempting to save to database...');
      const { data: savedLead, error: dbError } = await goldLeadsService.createLead(leadData);

      if (dbError) {
        console.error('Database error:', dbError);

        // Track failed form submission (invisible tracking)
        eventTrackingService.trackLeadFormSubmission(
          calculatorResult.weight,
          calculatorResult.purity,
          calculatorResult.ourOffer,
          false
        );

        throw new Error(`Database error: ${dbError.message}`);
      }

      console.log('Database save successful:', savedLead);

      // Track successful form submission (invisible tracking)
      eventTrackingService.trackLeadFormSubmission(
        calculatorResult.weight,
        calculatorResult.purity,
        calculatorResult.ourOffer,
        true
      );

      // Track Facebook Pixel Lead event
      if (typeof window !== 'undefined' && (window as any).fbq) {
        try {
          (window as any).fbq('track', 'Lead', {
            value: calculatorResult.ourOffer,
            currency: 'NOK',
            content_name: `Gold Lead - ${calculatorResult.purity}k ${calculatorResult.weight}g`,
            content_category: 'Gold Calculator Lead',
            status: 'completed'
          });
          console.log('Facebook Pixel Lead event tracked successfully');
        } catch (pixelError) {
          console.error('Facebook Pixel tracking error:', pixelError);
          // Don't fail the submission if pixel tracking fails
        }
      }

      // Send email notification (non-blocking)
      console.log('Attempting to send email...');
      let emailSuccess = false;
      try {
        const emailResponse = await fetch('/api/send-gold-lead-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(leadData)
        });

        if (emailResponse.ok) {
          console.log('Email sent successfully');
          emailSuccess = true;
        } else {
          const errorData = await emailResponse.json().catch(() => ({ error: 'Unknown error' }));
          console.error('Email sending failed:', errorData);
        }
      } catch (emailError) {
        console.error('Email sending exception:', emailError);
      }

      // Success - show success message (even if email failed, data is saved)
      console.log('=== FORM SUBMISSION SUCCESSFUL ===');
      console.log('Lead saved with ID:', savedLead?.id);
      setIsSubmitting(false);
      setSubmitSuccess(true);

      // If email failed, log warning but don't fail the entire submission
      if (!emailSuccess) {
        console.warn('Note: Lead saved successfully (ID: ' + savedLead?.id + '), but email notification failed.');
      }

      // Reset form after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
        setFormData({
          weight: '1',
          purity: '24',
          fullName: '',
          address: '',
          postalCode: '',
          city: '',
          phone: '',
          email: ''
        });
        setErrors({});
        hasStartedTyping.current = false; // Reset typing tracker
      }, 5000);

    } catch (error) {
      console.error('=== FORM SUBMISSION FAILED ===');
      console.error('Error details:', error);
      setIsSubmitting(false);

      const errorMessage = error instanceof Error ?
      error.message :
      'En ukjent feil oppstod. Vennligst prøv igjen eller kontakt support.';

      setSubmitError(errorMessage);

      // Scroll to error message
      setTimeout(() => {
        const errorElement = document.querySelector('[data-error-display]');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  return (
    <div id="priskalkulator" className="bg-background py-12 lg:py-20">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mb-12 text-center">
          
          <h2 className="font-headline text-3xl font-bold text-foreground lg:text-4xl">
            Beregn verdien av gullet ditt
          </h2>
        </div>

        <div className="grid gap-8 lg:grid-cols-2">
          {/* Right side - Calculator (First on mobile, second on desktop) */}
          <div className="order-1 lg:order-2 rounded-lg bg-card p-6 shadow-calculator lg:p-8">
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="mb-2 font-headline text-xl font-semibold text-foreground">Få blant Norges beste gullpriser – sjekk verdien nå

                </h3>
                {calculatorResult && calculatorResult.weight > 0 &&
                <div className="mb-4">
                    <div className="font-headline text-6xl font-bold text-[#F5A623] lg:text-7xl">
                      {Math.floor(calculatorResult.ourOffer).toLocaleString('nb-NO')}
                      <span className="ml-2 text-4xl">,-</span>
                    </div>
                    <p className="mt-2 font-body text-base text-muted-foreground">
                      Totalt for {calculatorResult.purity} karat
                    </p>
                  </div>
                }
              </div>

              {/* Karat selection buttons */}
              <div className="flex flex-wrap justify-center gap-3">
                {purityOptions.map((option) =>
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handlePurityClick(option.value)}
                  className={`rounded-full px-6 py-3 font-body text-base font-semibold transition-all duration-250 ${
                  formData.purity === option.value ?
                  'bg-accent text-accent-foreground shadow-md' :
                  'bg-muted text-muted-foreground hover:bg-muted/80'}`
                  }>

                    {option.label}
                  </button>
                )}
              </div>

              {/* Weight slider */}
              <div>
                <div className="mb-4 space-y-3">
                  <input
                    type="range"
                    id="weight"
                    name="weight"
                    value={formData.weight || '0'}
                    onChange={handleSliderChange}
                    min="1"
                    max="300"
                    step="1"
                    className="w-full h-3 bg-gray-300 rounded-lg appearance-none cursor-pointer touch-pan-y [&::-webkit-slider-track]:bg-gray-300 [&::-webkit-slider-track]:rounded-lg [&::-webkit-slider-track]:h-3 [&::-moz-range-track]:bg-gray-300 [&::-moz-range-track]:rounded-lg [&::-moz-range-track]:h-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-accent [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-accent [&::-moz-range-thumb]:border-0" />

                  <div className="flex items-center justify-end">
                    <div className="rounded-lg bg-accent px-4 py-2">
                      <span className="font-headline text-lg font-semibold text-accent-foreground">
                        {formData.weight || '0'}g
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <p className="font-body text-sm text-muted-foreground">Besøket er planlagt for en effektiv og diskret gjennomføring hjemme hos deg.

                </p>
              </div>
            </div>
          </div>

          {/* Left side - Lead Form (Second on mobile, first on desktop) */}
          <div className="order-2 lg:order-1 rounded-lg bg-card p-6 shadow-calculator lg:p-8">
            {submitSuccess ?
            <div className="rounded-lg bg-success/10 p-6 text-center">
                <Icon name="CheckCircleIcon" size={48} variant="solid" className="mx-auto mb-4 text-success" />
                <h3 className="mb-2 font-headline text-xl font-semibold text-foreground">
                  Takk for din bestilling!
                </h3>
                <p className="font-body text-base text-muted-foreground">
                  Vi behandler henvendelsen din fortløpende og kontakter deg snart for å bekrefte detaljene.
                </p>
              </div> :

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                <div className="mb-6">
                  <h3 className="font-headline text-2xl font-bold text-foreground lg:text-3xl">
                    bestill henting
                  </h3>
                  <p className="mt-2 font-body text-base text-muted-foreground">
Fyll ut skjemaet under for å avtale henting. Vi kontakter deg for å finne et tidspunkt som passer.
                  </p>
                </div>

                {/* Show general error message at top of form if validation fails */}
                {submitError && Object.keys(errors).length > 0 &&
              <div className="rounded-lg bg-error/10 p-4" data-error-display>
                    <div className="flex items-start space-x-3">
                      <Icon name="ExclamationCircleIcon" size={20} variant="solid" className="flex-shrink-0 text-error" />
                      <div className="flex-1">
                        <p className="font-body text-sm font-semibold text-error">Vennligst rett opp følgende:</p>
                        <ul className="mt-2 list-disc pl-5 space-y-1">
                          {Object.entries(errors).map(([field, error]) =>
                      <li key={field} className="font-body text-sm text-error">{error}</li>
                      )}
                        </ul>
                      </div>
                    </div>
                  </div>
              }

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    placeholder="For- og etternavn"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={`w-full rounded-lg border ${
                    errors.fullName ? 'border-error' : 'border-input'} bg-background px-4 py-3 font-body text-base text-foreground placeholder:text-muted-foreground transition-colors duration-250 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20`
                    } />

                    {errors.fullName &&
                  <p className="mt-1 font-body text-sm text-error">{errors.fullName}</p>
                  }
                  </div>

                  <div>
                    <input
                    type="text"
                    id="address"
                    name="address"
                    placeholder="Adresse"
                    value={formData.address}
                    onChange={handleInputChange}
                    className={`w-full rounded-lg border ${
                    errors.address ? 'border-error' : 'border-input'} bg-background px-4 py-3 font-body text-base text-foreground placeholder:text-muted-foreground transition-colors duration-250 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20`
                    } />

                    {errors.address &&
                  <p className="mt-1 font-body text-sm text-error">{errors.address}</p>
                  }
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    placeholder="Postnr"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    maxLength={4}
                    className={`w-full rounded-lg border ${
                    errors.postalCode ? 'border-error' : 'border-input'} bg-background px-4 py-3 font-body text-base text-foreground placeholder:text-muted-foreground transition-colors duration-250 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20`
                    } />

                    {errors.postalCode &&
                  <p className="mt-1 font-body text-sm text-error">{errors.postalCode}</p>
                  }
                  </div>

                  <div>
                    <input
                    type="text"
                    id="city"
                    name="city"
                    placeholder="Sted"
                    value={formData.city}
                    onChange={handleInputChange}
                    className={`w-full rounded-lg border ${
                    errors.city ? 'border-error' : 'border-input'} bg-background px-4 py-3 font-body text-base text-foreground placeholder:text-muted-foreground transition-colors duration-250 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20`
                    } />

                    {errors.city &&
                  <p className="mt-1 font-body text-sm text-error">{errors.city}</p>
                  }
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="Telefon"
                    value={formData.phone}
                    onChange={handleInputChange}
                    maxLength={8}
                    className={`w-full rounded-lg border ${
                    errors.phone ? 'border-error' : 'border-input'} bg-background px-4 py-3 font-body text-base text-foreground placeholder:text-muted-foreground transition-colors duration-250 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20`
                    } />

                    {errors.phone &&
                  <p className="mt-1 font-body text-sm text-error">{errors.phone}</p>
                  }
                  </div>

                  <div>
                    <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="E-post"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full rounded-lg border ${
                    errors.email ? 'border-error' : 'border-input'} bg-background px-4 py-3 font-body text-base text-foreground placeholder:text-muted-foreground transition-colors duration-250 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20`
                    } />

                    {errors.email &&
                  <p className="mt-1 font-body text-sm text-error">{errors.email}</p>
                  }
                  </div>
                </div>

                {/* Show submission error separately */}
                {submitError && Object.keys(errors).length === 0 &&
              <div className="rounded-lg bg-error/10 p-4" data-error-display>
                    <div className="flex items-start space-x-3">
                      <Icon name="ExclamationCircleIcon" size={20} variant="solid" className="flex-shrink-0 text-error" />
                      <div className="flex-1">
                        <p className="font-body text-sm font-semibold text-error">Innsending feilet</p>
                        <p className="mt-1 font-body text-sm text-error">{submitError}</p>
                        {submitError.includes('Database error') &&
                    <p className="mt-2 font-body text-xs text-muted-foreground">
                            Hvis problemet vedvarer, vennligst kontakt support@gullklar.no
                          </p>
                    }
                      </div>
                    </div>
                  </div>
              }

                <button
                type="submit"
                disabled={isSubmitting}
                onClick={() => {
                  // Track calculator button click when form is submitted (invisible tracking)
                  if (calculatorResult && calculatorResult.weight > 0) {
                    eventTrackingService.trackCalculatorClick(
                      calculatorResult.weight,
                      calculatorResult.purity,
                      calculatorResult.ourOffer
                    );
                  }
                }}
                className="flex w-full items-center justify-center space-x-2 rounded-full bg-accent px-8 py-4 font-cta text-base font-semibold text-accent-foreground shadow-cta transition-all duration-250 hover:bg-accent/90 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-accent disabled:hover:shadow-cta lg:text-lg">

                  {isSubmitting ?
                <>
                      <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
                      <span>Sender bestilling...</span>
                    </> :

                <span>Kontakt oss</span>
                }
                </button>

                <p className="text-center font-body text-xs text-muted-foreground">
                  Prisene er veiledende og kan variere basert på gullkvalitet og markedsforhold
                </p>
              </form>
            }
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="font-body text-sm text-muted-foreground">
            Ved å sende inn dette skjemaet godtar du våre{' '}
                  <a href="/vilkar" className="text-primary underline hover:text-primary/80">
                    vilkår og betingelser
                  </a>
                </p>
        </div>
      </div>
    </div>);

};

export default GoldPriceCalculator;